<template>
  <div class="ai-init-initializing">
    <div class="ai-init-initializing__header">
      <div class="ai-init-initializing__header-title">
        <i class="ai-init-initializing__header-icon icon-sparkling-2-fill t-iconfont"></i>
        <span>AI 智能生成企业配置中...</span>
      </div>
      <div class="ai-init-initializing__header-desc">
        预计 2min 内完成，可稍后再来配置，无需在此等候
      </div>
    </div>
    <div class="ai-analysis-loading">
      <div class="ai-analysis-loading__grid"></div>
      <div class="ai-analysis-loading__content">
        <div class="ai-analysis-loading__card">
          <div
            v-for="(item, index) in mockData"
            :key="item.id"
            class="ai-analysis-loading__step"
            :class="stepItemClassMap[item.type]"
          >
            <div class="ai-analysis-loading__track">
              <div class="ai-analysis-loading__status" :class="stepStatusClassMap[item.type]">
                <i
                  v-if="item.type === 1"
                  class="ai-analysis-loading__success-icon icon-checkbox-circle-line t-iconfont"
                ></i>
                <template v-else>{{ index + 1 }}</template>
              </div>
              <div v-if="item.type === 2" class="ai-analysis-loading__line"></div>
            </div>
            <div class="ai-analysis-loading__main">
              <div
                class="ai-analysis-loading__title"
                :class="{ 'ai-analysis-loading__title--secondary': item.type === 1 }"
              >
                {{ item.title }}
              </div>

              <clueCardLoading
                v-if="item.type === 2 && item.desc"
                :text="item.desc"
              ></clueCardLoading>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import clueCardLoading from '@/components/card-loading.vue'
import { computed } from 'vue'
import { useAppStore } from '@/store/useAppStore'

const appStore = useAppStore()

const baseSteps = [
  {
    id: '1',
    title: '公司业务说明',
    desc: '正在读取企业，联网解析企业核心...'
  },
  {
    id: '2',
    title: '跟进数据管理',
    desc: '正在启用已接入的数据源...'
  },
  {
    id: '3',
    title: '跟进助手配置-跟进内容提炼',
    desc: '正在生成跟进内容提炼模版...'
  },
  {
    id: '4',
    title: '跟进助手配置-质量分析规则',
    desc: '正在生成质量分析规则...'
  }
]

const stepItemClassMap = {
  1: 'ai-analysis-loading__step--finished',
  2: 'ai-analysis-loading__step--active',
  3: 'ai-analysis-loading__step--pending'
}

const stepStatusClassMap = {
  1: 'ai-analysis-loading__status--success',
  2: 'ai-analysis-loading__status--active',
  3: 'ai-analysis-loading__status--pending'
}

const mockData = computed(() => {
  const status = Number(appStore.isAiInitialization)
  const typeListMap = {
    1: [1, 2, 3, 3],
    2: [1, 1, 2, 3],
    3: [1, 1, 1, 2],
    4: [1, 1, 1, 1]
  }
  const typeList = typeListMap[status] || [3, 3, 3, 3]

  return baseSteps.map((item, index) => ({
    ...item,
    type: typeList[index]
  }))
})
</script>

<style lang="scss" scoped>
.ai-init-initializing {
  width: 100%;
  height: 100%;
  flex: 1;
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  box-sizing: border-box;
  border: 1px solid #e5e6eb;
  overflow: hidden;

  &__header {
    flex: 0 0 auto;
    padding: 16px 24px 14px;
    background: #fff;
  }

  &__header-title {
    display: flex;
    align-items: center;
    color: #323233;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
  }

  &__header-icon {
    margin-right: 8px;
    font-size: 16px;
    line-height: 16px;
    background: linear-gradient(314deg, #1574ff 2%, #5952ff 51%, #a969fe 96%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  &__header-desc {
    margin-top: 2px;
    color: #646566;
    font-size: 13px;
    line-height: 18px;
  }
}
.ai-analysis-loading {
  --grid-size: 16px;
  --grid-color: rgba(220, 225, 235, 0.6);
  --bg-color: #f8faff;
  --violet-bg: #f5e8ff;
  --violet-color: #7a52f4;
  --success-color: #00c241;
  --line-light: #f2f3f5;
  --line-normal: #e5e6eb;
  --text-main: #323233;
  --text-secondary: #969799;
  --fill-light: #f7f8fa;

  position: relative;
  margin: 0 16px 16px 16px;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 24px;
  background-color: var(--bg-color);
  border: 1px solid #eef0f5;
  border-radius: 16px;
  user-select: none;

  &::after {
    position: absolute;
    inset: 0;
    z-index: 2;
    content: '';
    background: radial-gradient(circle at center, transparent 20%, var(--bg-color) 95%);
    pointer-events: none;
  }

  &__grid {
    position: absolute;
    inset: 0;
    z-index: 1;
    background-image:
      linear-gradient(var(--grid-color) 1px, transparent 1px),
      linear-gradient(90deg, var(--grid-color) 1px, transparent 1px);
    background-size: var(--grid-size) var(--grid-size);
    animation: moveGridUp 1s linear infinite;
    pointer-events: none;
  }

  &__content {
    position: relative;
    z-index: 3;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__card {
    width: 428px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.72);
    border: 1px solid var(--violet-bg);
    border-radius: 16px;
    backdrop-filter: blur(6px);
  }

  &__step {
    display: flex;
    gap: 8px;

    & + & {
      margin-top: 12px;
    }
  }

  &__track {
    width: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
  }

  &__status {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    border-radius: 50%;
    font-size: 12px;
    line-height: 16px;

    &--success {
      color: var(--success-color);
    }

    &--active {
      font-weight: 600;
      color: var(--violet-color);
      background: var(--violet-bg);
      border: 1px solid var(--violet-color);
    }

    &--pending {
      font-weight: 600;
      color: var(--text-secondary);
      background: var(--fill-light);
      border: 1px solid var(--line-normal);
    }
  }

  &__success-icon {
    font-size: 20px;
  }

  &__line {
    width: 1px;
    flex: 1;
    min-height: 8px;
    margin-top: 8px;
    background: var(--line-light);
  }

  &__main {
    flex: 1;

    :deep(.clue-card-loading) {
      margin-top: 8px;
      margin-bottom: 0;
    }
  }

  &__title {
    min-height: 22px;
    color: var(--text-main);
    font-size: 16px;
    line-height: 22px;

    &--secondary {
      color: var(--text-secondary);
    }
  }
}

@keyframes moveGridUp {
  0% {
    background-position: 0 0;
  }

  100% {
    background-position: 0 calc(-1 * var(--grid-size));
  }
}
</style>
